/**
 * PATH: packages/email-hub/src/feature/reader-header/model/buildDecisionQueue.ts
 * FILE_ID: EMAILHUB-READERHEADER-MODEL-DECISIONS-001
 * PURPOSE: Build one ordered review queue from competing reader suggestions.
 * ROLE IN ARCHITECTURE: Pure decision selector between email state and UI.
 * MAIN EXPORTS: buildReaderDecisionQueue, advanceDecisionQueue, filterResolvedDecisions.
 * NON-RESPONSIBILITIES: This file does not accept, reject, or persist decisions.
 * NOTES FOR FUTURE AI: One visible decision is a product invariant; add new kinds via
 *   READER_DECISION_PRIORITY, never with an inline priority number. Every decision
 *   carries a visibilityImpact sentence — the Review Queue renders it verbatim.
 */
import type { Email } from '../../hooks/useEmails'
import { READER_DECISION_PRIORITY, type ReaderDecision } from './types'
import {
  placementNeedsReview,
  selectReaderPlacements,
} from './placements'

const BILL_CATEGORIES = new Set(['bill', 'supplier', 'invoice'])

/**
 * Below this classifier confidence an unlocked AI/fallback label asks for an
 * operator decision; at or above it the label is trusted silently.
 */
export const CLASSIFICATION_REVIEW_BELOW = 0.75

/** Workflow-label provenances that are machine guesses (rules are deterministic). */
const MACHINE_LABEL_SOURCES = new Set(['llm', 'b0_fallback'])

export interface DecisionQueueInput {
  email: Pick<
    Email,
    | 'id'
    | 'category'
    | 'category_locked'
    | 'has_attachments'
    | 'organisation'
    | 'organisation_name'
    | 'allocation_summary'
    | 'allocation_summaries'
    | 'suggested_org'
    | 'suggested_job'
    | 'workflow_label'
  >
  shellOrgId?: number | null
  /**
   * How many organisations the signed-in user is a member of.
   *
   * Only cross-organisation decisions read this. A single-org operator can
   * never act on "file this somewhere else" — there is nowhere else — so
   * offering the choice is pure noise (operator 2026-07-26). Absent/unknown
   * counts fail CLOSED: the suggestion stays hidden until membership is
   * known, matching ReferToOrgAction's `< 2 → render nothing` precedent.
   */
  memberOrgCount?: number | null
}

/** Cross-organisation suggestions are meaningless below this membership count. */
export const CROSS_ORG_MIN_MEMBERSHIPS = 2

/**
 * Build highest-risk-first decisions for the single decision line.
 *
 * Inputs are API state plus the active shell organisation. Output is stable,
 * highest priority first, each decision carrying accept/reject effects, a
 * visibility-impact sentence, a permission gate, and a stale-version token.
 * Side effects: none. Invalid optional suggestions are ignored rather than
 * producing dead controls.
 */
export function buildReaderDecisionQueue({
  email,
  shellOrgId,
  memberOrgCount,
}: DecisionQueueInput): ReaderDecision[] {
  const decisions: ReaderDecision[] = []
  const isMultiOrgMember =
    (memberOrgCount ?? 0) >= CROSS_ORG_MIN_MEMBERSHIPS

  if (
    shellOrgId != null &&
    email.organisation != null &&
    shellOrgId !== email.organisation
  ) {
    decisions.push({
      id: `organisation:${email.id}`,
      kind: 'organisation',
      priority: READER_DECISION_PRIORITY.organisation,
      label: 'Organisation mismatch',
      target: email.organisation_name || `Organisation ${email.organisation}`,
      explanation:
        'Move or switch organisation before changing filing so tenant boundaries stay explicit.',
      acceptEffect:
        'Opens Move organisation with an explicit revoke warning; nothing moves silently.',
      rejectEffect: 'Keeps the email in its home organisation; filing stays blocked here.',
      visibilityImpact:
        'Moving changes which organisation’s members can see this email; staying changes nothing.',
      reversible: false,
      permission: 'org.move',
      versionToken: `org:${email.organisation}`,
    })
  }

  if (BILL_CATEGORIES.has(email.category ?? '')) {
    decisions.push({
      id: `bill:${email.id}`,
      kind: 'bill',
      priority: READER_DECISION_PRIORITY.bill,
      label: 'Supplier bill',
      target: 'Bills · AR/AP',
      explanation:
        'Confirm the financial classification or reject it before the email is routed to Bills.',
      // The drawer promise depends on a real invoice document existing — an
      // attachment-less bill must never toast "opening…" (legacy contract).
      acceptEffect: email.has_attachments
        ? 'Confirms Bill, starts Bills allocation, and opens the source invoice.'
        : 'Confirms Bill and starts Bills allocation.',
      rejectEffect: 'Changes classification to General with Undo preserved.',
      visibilityImpact:
        'Accept files it where Bills · AR/AP board members can see it; Reject changes nothing.',
      reversible: true,
      permission: 'email.write',
      versionToken: `category:${email.category ?? ''}`,
    })
  }

  if (email.category === 'quote_request') {
    decisions.push({
      id: `estimator:${email.id}`,
      kind: 'estimator',
      priority: READER_DECISION_PRIORITY.estimator,
      label: 'Quote request',
      target: 'Allocate estimator',
      explanation:
        'A tender needs an owner — hand this quote request to an estimator to prepare it.',
      acceptEffect:
        'Allocates the picked estimator and emails them the handoff notification.',
      rejectEffect: 'Reclassifies to General; the quote-request prompt stops.',
      visibilityImpact:
        'Allocating grants the estimator visibility of this email; rejecting changes nothing.',
      reversible: true,
      permission: 'email.write',
      versionToken: `category:${email.category}`,
    })
  }

  selectReaderPlacements(email)
    .filter(placementNeedsReview)
    .forEach((placement) => {
      decisions.push({
        id: `placement:${placement.allocation_id}`,
        kind: 'placement',
        priority: READER_DECISION_PRIORITY.placement,
        label: placement.needs_review ? 'Review filing' : 'Confirm filing',
        target: `${placement.board_name} · ${placement.lane_name}`,
        explanation:
          'Confirm the suggested card placement or move it before normal triage continues.',
        acceptEffect: 'Confirms this placement; the card stays on its board and lane.',
        rejectEffect: 'Removes the suggested placement; the card returns to review.',
        visibilityImpact:
          'Accept keeps the card visible to that board’s members; Reject removes it from the board.',
        reversible: true,
        permission: 'kanban.write',
        versionToken: `placement:${placement.allocation_id}:${placement.card_status ?? ''}:${placement.lane_id}`,
        placement,
      })
    })

  // Multi-org members only. The backend still suggests an organisation for
  // everyone, so the gate lives here rather than in the payload — a single-org
  // operator has no second organisation to grant access to, and the review
  // asked them to arbitrate a transfer that could not happen.
  if (email.suggested_org?.org_id && isMultiOrgMember) {
    decisions.push({
      id: `suggested-org:${email.suggested_org.org_id}`,
      kind: 'suggested_org',
      priority: READER_DECISION_PRIORITY.suggested_org,
      label: 'Suggested organisation',
      target: email.suggested_org.org_name,
      explanation: email.suggested_org.reason || 'The sender or thread matches this organisation.',
      acceptEffect:
        'Starts the explicit grant/transfer flow to the suggested organisation.',
      rejectEffect: 'Dismisses the suggestion; the email stays in its home organisation.',
      visibilityImpact:
        'Accept grants the suggested organisation access through the audited transfer flow.',
      reversible: true,
      permission: 'org.move',
      versionToken: `suggested-org:${email.suggested_org.org_id}:${email.suggested_org.kind}`,
    })
  }

  if (email.suggested_job?.job_id) {
    decisions.push({
      id: `suggested-job:${email.suggested_job.job_id}`,
      kind: 'suggested_job',
      priority: READER_DECISION_PRIORITY.suggested_job,
      label: 'Suggested job',
      target: email.suggested_job.job_label,
      explanation: `Matched from ${email.suggested_job.kind} evidence.`,
      acceptEffect: 'Links the email to the suggested job.',
      rejectEffect: 'Dismisses the suggestion without linking.',
      visibilityImpact: 'No visibility change; job links are internal metadata.',
      reversible: true,
      permission: 'email.write',
      versionToken: `suggested-job:${email.suggested_job.job_id}:${email.suggested_job.score}`,
    })
  }

  const label = email.workflow_label
  if (
    label &&
    MACHINE_LABEL_SOURCES.has(label.source) &&
    label.confidence < CLASSIFICATION_REVIEW_BELOW &&
    !email.category_locked &&
    email.category &&
    email.category !== 'quote_request' &&
    !BILL_CATEGORIES.has(email.category)
  ) {
    decisions.push({
      id: `classification:${email.id}`,
      kind: 'classification',
      priority: READER_DECISION_PRIORITY.classification,
      label: 'Classification suggestion',
      target: label.category.replace(/_/g, ' '),
      explanation: `The ${label.source === 'llm' ? 'AI classifier' : 'fallback classifier'} labelled this at ${Math.round(label.confidence * 100)}% confidence.`,
      acceptEffect:
        'Locks the current classification so re-classification cannot change it.',
      rejectEffect: 'Reclassifies to General with Undo preserved.',
      visibilityImpact: 'No visibility change; classification only affects routing.',
      reversible: true,
      permission: 'email.write',
      versionToken: `classification:${label.category}:${label.confidence}`,
    })
  }

  return decisions.sort(
    (left, right) =>
      right.priority - left.priority || left.id.localeCompare(right.id),
  )
}

/**
 * Advance the queue after one decision resolves (accept or reject).
 *
 * Inputs: the current queue plus the resolved decision id. Output: the
 * remaining queue in unchanged priority order — the next item becomes the
 * single visible decision without spawning a second bar (plan D269-12).
 * Side effects: none. An unknown id returns the queue untouched.
 */
export function advanceDecisionQueue(
  queue: readonly ReaderDecision[],
  resolvedId: string,
): ReaderDecision[] {
  return queue.filter((decision) => decision.id !== resolvedId)
}

/**
 * Drop every locally resolved decision from a freshly built queue.
 *
 * Inputs: the pure queue plus the ids the operator resolved this session
 * (optimistic advance before the server echo). Output: the surviving queue in
 * priority order. Side effects: none. Unknown ids are ignored so a stale
 * local id can never hide an unrelated decision.
 */
export function filterResolvedDecisions(
  queue: readonly ReaderDecision[],
  resolvedIds: readonly string[],
): ReaderDecision[] {
  if (resolvedIds.length === 0) return [...queue]
  const resolved = new Set(resolvedIds)
  return queue.filter((decision) => !resolved.has(decision.id))
}
